import { cn } from '@/lib/utils';
import { useI18n } from '@/providers/i18n';
import { Video as VideoIcon } from 'lucide-react';
import { videoModels } from '@flowmuse/models/video';
import { VariantImage } from '@/components/variant-image';
import { videoPosterUrl } from '@flowmuse/api-core/media-shared';
import { resolveAssetRefPreview } from '@flowmuse/models/shared/asset-ref';
import { useMemo } from 'react';

const SUMMARY_THUMB_TRANSFORM = { width: 128, height: 128, resize: 'cover', quality: 80 } as const;

type VideoCollapsedSummaryProps = {
  data: Record<string, any> | undefined;
  className?: string;
};

const fmtSec = (n: number) => (Number.isInteger(n) ? `${n}s` : `${Math.round(n * 10) / 10}s`);

const formatMaybeSeconds = (value: unknown): string | undefined => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return fmtSec(value);
  }
  if (typeof value === 'string' && value.trim()) {
    const raw = value.trim();
    const n = Number(raw);
    if (Number.isFinite(n) && n > 0) return fmtSec(n);
    return raw;
  }
  return undefined;
};

export const VideoCollapsedSummary = ({ data, className }: VideoCollapsedSummaryProps) => {
  const { t } = useI18n();
  const d = (data ?? {}) as any;

  const modelId = typeof d.model === 'string' && d.model ? d.model : undefined;
  const modelLabel = (() => {
    if (!modelId) return undefined;
    try {
      const direct = (videoModels as any)?.[modelId]?.label;
      if (typeof direct === 'string' && direct.trim()) return direct.trim();
      // 兼容历史数据：有些节点可能存成 "provider/modelId"
      if (modelId.includes('/')) {
        const tail = modelId.split('/').pop();
        if (tail) {
          const alt = (videoModels as any)?.[tail]?.label;
          if (typeof alt === 'string' && alt.trim()) return alt.trim();
        }
      }
    } catch {}
    return modelId;
  })();
  const aspectRatio =
    (typeof d.aspectRatio === 'string' && d.aspectRatio) ||
    (typeof d.generated?.aspectRatio === 'string' && d.generated.aspectRatio) ||
    undefined;
  const resolution =
    (typeof d.resolution === 'string' && d.resolution) ||
    (typeof d.quality === 'string' && d.quality) ||
    (typeof d.size === 'string' && d.size) ||
    undefined;

  const duration =
    formatMaybeSeconds(d.duration) ??
    formatMaybeSeconds(d.durationSeconds) ??
    formatMaybeSeconds(d.generated?.durationSeconds) ??
    undefined;

  const generatedUrl = (() => {
    const g = d.generated;
    if (g && typeof g === 'object') {
      // 提交审核后 url 为 Asset:// 引用，用 assetPreviewUrl 推导首帧图
      const url = resolveAssetRefPreview(
        typeof g.url === 'string' ? g.url : undefined,
        typeof d.assetPreviewUrl === 'string' ? d.assetPreviewUrl : undefined,
      );
      const typ = g.type;
      if (typeof url === 'string' && url) {
        // 仅当 generated 类型像 video 才算“已生成”
        if (typeof typ === 'string' && typ.startsWith('video')) return url;
        // 没有 type 时也保守认为是生成结果（兼容历史数据）
        if (typeof typ === 'undefined') return url;
      }
    }
    return undefined;
  })();

  const thumbUrlFromData =
    (typeof d.generated?.thumbnailUrl === 'string' && d.generated.thumbnailUrl) ||
    (typeof d.generated?.coverUrl === 'string' && d.generated.coverUrl) ||
    (typeof d.generated?.previewImageUrl === 'string' && d.generated.previewImageUrl) ||
    undefined;
  const autoText = t('Auto');


  // 缩略图：优先用节点数据；否则从视频 URL 推导首帧图
  const posterUrl = useMemo(
    () =>
      thumbUrlFromData ??
      (generatedUrl ? videoPosterUrl(generatedUrl, 'first') : undefined) ??
      (typeof d?.content?.url === 'string' ? videoPosterUrl(String(d.content.url), 'first') : undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [thumbUrlFromData, generatedUrl, d?.content?.url],
  );

  return (
    <div className={cn('flex h-full w-full items-start gap-4 p-3', className)}>
      <div className="h-32 w-32 shrink-0 overflow-hidden rounded-2xl border border-sidebar-border/30 bg-sidebar-accent/30 flex items-center justify-center shadow-inner">
        {/* 仅渲染首帧图（不加载 / 解码视频）：优先 media 服务预生成的缩略图，失败回退首帧原图，再失败显示图标 */}
        <VariantImage
          url={posterUrl ?? ''}
          transform={SUMMARY_THUMB_TRANSFORM}
          alt={t('Video thumbnail')}
          className="h-full w-full object-cover transition-transform duration-500 hover:scale-110"
          loading="lazy"
          decoding="async"
          fallback={<VideoIcon size={28} className="text-muted-foreground/40" />}
        />
      </div>

      <div className="min-w-0 flex-1 flex flex-col h-32">
        <div className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/30 mb-1">{t('Video')}</div>
        <div className="text-sm font-bold truncate text-foreground/90">{modelLabel ?? '-'}</div>

        <div className="mt-2 flex flex-1 flex-col text-[11px] leading-snug">
          {/* 中间参数：包裹在固定高度容器（与图像节点保持一致的占位高度） */}
          <div className="grid grid-rows-3 gap-1">
            <div className="truncate">
              <span className="text-muted-foreground/60">{t('Aspect ratio')}:</span>{' '}
              <span className="text-foreground/80 font-medium">{aspectRatio ?? autoText}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-muted-foreground/60">{t('Resolution')}:</span>
              <span className="text-foreground/80 font-mono tabular-nums text-[10px]">{resolution ?? autoText}</span>
            </div>
            <div className="truncate">
              <span className="text-muted-foreground/60">{t('Duration')}:</span>{' '}
              <span className="text-foreground/80 font-medium">{duration ?? autoText}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


